import { Box, Flex, Grid, Heading, Input, Select } from 'theme-ui';

import { ErrorLabel } from '@/components/Collection/Error';
import { CollectionBannerFontStyle } from '@/graphql/types/generated';

type Row = {
  label: string;
  positions: string[];
  blocks: { span: number; positionId?: string }[];
};

const ROWS: Row[] = [
  {
    label: 'Row 1',
    blocks: [
      { span: 3, positionId: '1A' },
      { span: 3, positionId: '1B' },
    ],
    positions: ['1A', '1B'],
  },
  {
    label: 'Row 2',
    positions: ['2'],
    blocks: [
      { span: 1 },
      { span: 1 },
      { span: 1 },
      { span: 1 },
      { span: 1 },
      { span: 1 },
    ],
  },
  {
    label: 'Row 3',
    positions: ['3'],
    blocks: [{ span: 2 }, { span: 1 }, { span: 1 }, { span: 1 }, { span: 1 }],
  },
  {
    label: 'Row 4',
    blocks: [
      { span: 1 },
      { span: 1 },
      { span: 1 },
      { span: 1 },
      { span: 1 },
      { span: 1 },
    ],
    positions: ['4'],
  },
  {
    label: 'Row 5',
    positions: ['5'],
    blocks: [{ span: 2 }, { span: 1 }, { span: 1 }, { span: 1 }, { span: 1 }],
  },
  {
    label: 'Row 6',

    blocks: [
      { span: 1 },
      { span: 1 },
      { span: 1 },
      { span: 1 },
      { span: 1 },
      { span: 1 },
    ],
    positions: ['6'],
  },
  {
    label: 'Row 7',
    positions: ['7'],
    blocks: [{ span: 2 }, { span: 1 }, { span: 1 }, { span: 1 }, { span: 1 }],
  },
];

// eslint-disable-next-line complexity
export default function ConfigureLaunch({
  label,
  endDate,
  disabled,
  position,
  submitted,
  startDate,
  fontStyle,
  hidePosition,
  onEndDateChange,
  onPositionChange,
  onStartDateChange,
  onFontStyleChange,
  allowedPositions,
}: {
  label: string;
  endDate: string;
  position: string;
  startDate: string;
  disabled?: boolean;
  fontStyle?: string;
  submitted?: boolean;
  hidePosition?: boolean;
  allowedPositions: string[];
  onEndDateChange: (date: string) => void;
  onStartDateChange: (date: string) => void;
  onPositionChange: (position: string) => void;
  onFontStyleChange?: (fontStyle: string) => void;
}) {
  const allowed = new Set(allowedPositions);
  const isRowEnabled = (row: Row) => row.positions.some((p) => allowed.has(p));
  const allPositions = ROWS.filter(isRowEnabled).flatMap((row) =>
    row.positions.filter((p) => allowed.has(p)),
  );

  const endDateError = !endDate
    ? 'Required'
    : startDate && endDate <= startDate
    ? 'End date must be after start date'
    : undefined;

  return (
    <Flex sx={{ flexDirection: 'column', gap: 4 }}>
      <Flex sx={{ gap: 2 }}>
        <Heading variant="headingSm">{label}</Heading>
      </Flex>
      <Grid
        sx={{ gap: 4, gridTemplateColumns: hidePosition ? '1fr' : '2fr 1fr' }}
      >
        {!hidePosition && (
          <div sx={{ flex: 1 }}>
            <Heading variant="headingXs">Layout</Heading>
            <Flex sx={{ flexDirection: 'column', gap: 2 }}>
              {ROWS.map((row) => {
                const enabled = isRowEnabled(row);
                const hasMultiplePositions =
                  row.positions.filter((p) => allowed.has(p)).length > 1;
                const isRowSelected =
                  !hasMultiplePositions &&
                  row.positions.length === 1 &&
                  position === row.positions[0];
                return (
                  <Flex
                    key={row.label}
                    onClick={() => {
                      if (enabled && row.positions.length === 1) {
                        onPositionChange(row.positions[0]);
                      }
                    }}
                    sx={{
                      p: 2,
                      gap: 2,
                      border: '1px solid',
                      alignItems: 'center',
                      borderColor: 'lightgray',
                      backgroundColor: '#EEEBE6',
                      cursor: !enabled
                        ? 'not-allowed'
                        : row.positions.length === 1
                        ? 'pointer'
                        : 'default',
                      opacity: !enabled ? 0.3 : 1,
                    }}
                  >
                    <Box sx={{ minWidth: 44, fontSize: 1 }}>{row.label}</Box>
                    <Grid
                      sx={{
                        gap: 3,
                        flex: 1,
                        gridTemplateColumns: 'repeat(6, 1fr)',
                      }}
                    >
                      {row.blocks.map((block, i) => {
                        const isBlockSelected = block.positionId
                          ? position === block.positionId
                          : isRowSelected;
                        return (
                          <Box
                            key={i}
                            onClick={(e) => {
                              if (
                                hasMultiplePositions &&
                                block.positionId &&
                                allowed.has(block.positionId)
                              ) {
                                e.stopPropagation();
                                onPositionChange(block.positionId);
                              }
                            }}
                            sx={{
                              height: 24,
                              border: '1px solid',
                              borderColor: 'lightgray',
                              backgroundColor: isBlockSelected
                                ? 'primaryDefault'
                                : 'white',
                              gridColumn: `span ${block.span}`,
                              cursor:
                                hasMultiplePositions &&
                                block.positionId &&
                                allowed.has(block.positionId)
                                  ? 'pointer'
                                  : 'inherit',
                            }}
                          />
                        );
                      })}
                    </Grid>
                  </Flex>
                );
              })}
            </Flex>
          </div>
        )}
        <Flex sx={{ gap: 3, flexDirection: 'column' }}>
          {!hidePosition && (
            <div>
              <Heading variant="headingXs">Choose Position</Heading>
              <Select
                value={position}
                disabled={disabled}
                onChange={(event) => onPositionChange(event.target.value)}
                sx={{
                  height: '40px',
                  borderColor:
                    submitted && !position
                      ? 'errorDefault'
                      : 'secondaryHighlight',
                }}
              >
                <option value="">Select a position...</option>
                {allPositions.map((pos) => (
                  <option key={pos} value={pos}>
                    {pos}
                  </option>
                ))}
              </Select>
              <ErrorLabel
                message={submitted && !position ? 'Required' : undefined}
              />
            </div>
          )}
          {onFontStyleChange && (
            <FontStyleSelect
              disabled={disabled}
              submitted={submitted}
              fontStyle={fontStyle ?? ''}
              onChange={onFontStyleChange}
            />
          )}
          <div>
            <Heading variant="headingXs">Start Date</Heading>
            <Input
              type="date"
              value={startDate}
              disabled={disabled}
              onChange={(event) => onStartDateChange(event.target.value)}
              sx={{
                borderColor:
                  submitted && !startDate ? 'errorDefault' : undefined,
              }}
            />
            <ErrorLabel
              message={submitted && !startDate ? 'Required' : undefined}
            />
          </div>
          <div>
            <Heading variant="headingXs">End Date</Heading>
            <Input
              type="date"
              value={endDate}
              disabled={disabled}
              min={startDate || undefined}
              onChange={(event) => onEndDateChange(event.target.value)}
              sx={{
                borderColor:
                  submitted && endDateError ? 'errorDefault' : undefined,
              }}
            />
            <ErrorLabel message={submitted ? endDateError : undefined} />
          </div>
        </Flex>
      </Grid>
    </Flex>
  );
}

function FontStyleSelect({
  disabled,
  onChange,
  fontStyle,
  submitted,
}: {
  fontStyle: string;
  disabled?: boolean;
  submitted?: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <div>
      <Heading variant="headingXs">Font Style</Heading>
      <Select
        value={fontStyle}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        sx={{
          height: '40px',
          borderColor:
            submitted && !fontStyle ? 'errorDefault' : 'secondaryHighlight',
        }}
      >
        <option value="">Select font style...</option>
        {Object.values(CollectionBannerFontStyle).map((style) => (
          <option key={style} value={style}>
            {style.charAt(0) + style.slice(1).toLowerCase()}
          </option>
        ))}
      </Select>
      <ErrorLabel message={submitted && !fontStyle ? 'Required' : undefined} />
    </div>
  );
}
